import { MdRefresh } from "react-icons/md";

import "../history.css";
import {
  EntryDate,
  HistoryFoot,
  HistoryToolbar,
  ProductChips,
  TableState,
  Text,
  groupRows,
  useHistoryQuery,
} from "./historyCommon";

/* ------------------------------------------------------------------ *
 * Customers history (redesign) — /customer-history
 *
 * Replaces src/pages/customersHistory.js (kept at
 * /customer-history-v1), which was workHistory.js with different column
 * headers — right down to `document.title = 'Work History'`.
 *
 * What changes:
 *   - the customer was the second column behind the late person's name,
 *     on a page about customers        -> the customer leads, with who
 *                                         the order was for underneath
 *   - the funeral email column was the
 *     literal string "N/A" for every
 *     row                              -> `payment_email`, the field the
 *                                         order form collects for both
 *                                         types
 *   - phone and email were flat text   -> tel: / mailto: links, since
 *                                         calling the customer back is
 *                                         the reason to open this page
 *   - a 5%-wide empty spacer column    -> gone
 *   - purchased products were two
 *     `shorten_product_list` runs
 *     joined by a comma, so a funeral
 *     order read
 *     "2 x CC, , 1 x Wreath" when one
 *     side was empty                   -> one chip per line item, both
 *                                         selections in one cell
 *
 * Query, pager and chips come from ./historyCommon.js, shared with
 * /work-history.
 * ------------------------------------------------------------------ */

const COLUMNS = [
  { key: "num", label: "#", className: "hx-num" },
  { key: "customer", label: "Customer" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "invoice", label: "Invoice" },
  { key: "products", label: "Purchased products" },
  { key: "date", label: "Ordered" },
];

/* A funeral order's customer is whoever placed it; the order itself is
   for the late person. A retail order's customer is the company when
   there is one, otherwise the person who ordered. */
function customerOf(order, orderType) {
  if (orderType === "Funeral") {
    return {
      name: order.ordered_by,
      sub: order.late_person_name ? `for ${order.late_person_name}` : "",
    };
  }
  return {
    name: order.company_name || order.ordered_by,
    sub: [order.company_name ? order.ordered_by : "", order.recipient_name && `to ${order.recipient_name}`]
      .filter(Boolean)
      .join(" · "),
  };
}

function Link({ value, href, empty }) {
  if (!value) return <span className="hx-empty-v">{empty}</span>;
  return <a href={href + value}>{value}</a>;
}

function CustomerRow({ order, orderType, num }) {
  const customer = customerOf(order, orderType);
  const groups =
    orderType === "Funeral"
      ? [order.casket_cover_selected, order.tributes_selected]
      : [order.products_selected];

  return (
    <tr className="hx-row">
      <td className="hx-num">{num}</td>
      <td className="hx-lead">
        <span className="hx-lead-name">
          <Text value={customer.name} empty="Unnamed customer" />
        </span>
        {customer.sub && <span className="hx-lead-sub">{customer.sub}</span>}
      </td>
      <td className="hx-contact">
        <Link value={order.orderer_phone} href="tel:" empty="No number" />
      </td>
      <td className="hx-contact">
        <Link value={order.payment_email} href="mailto:" empty="No email" />
      </td>
      <td className="hx-plain">
        <Text value={order.invoice_number} empty="—" />
      </td>
      <td className="hx-prods">
        <ProductChips groups={groups} empty="None recorded" />
      </td>
      <td>
        <EntryDate value={order.date || order.timestamp} />
      </td>
    </tr>
  );
}

export function CustomersHistoryRedesign() {
  const q = useHistoryQuery("Retail");
  const rows = groupRows(q.orders);
  const dateWord = q.orderType === "Funeral" ? "funeral" : "delivery";

  return (
    <div className="hx-page">
      <HistoryToolbar
        q={q}
        searchPlaceholder="Search by customer, company, phone or invoice"
      >
        <button
          type="button"
          className="rf-btn rf-btn-ghost"
          onClick={q.refresh}
          title="Reload"
        >
          <MdRefresh style={{ verticalAlign: "-2px" }} /> Reload
        </button>
      </HistoryToolbar>

      <div className="hx-body">
        <p className="hx-lede">
          Who bought what, newest order first. One row per order — a customer
          who has ordered twice appears twice.{" "}
          {q.date ? (
            <>
              Filtered to a {dateWord} date of <b>{q.date}</b>.
            </>
          ) : (
            <>All {dateWord} dates.</>
          )}
          {q.query && (
            <>
              {" "}
              Matching <b>“{q.query}”</b>.
            </>
          )}
        </p>

        <div className="hx-tablewrap">
          <div className="hx-scroll">
            <table className="hx-table">
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c.key} className={c.className}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <TableState
                  q={q}
                  cols={COLUMNS.length}
                  emptyLead={
                    q.query || q.date
                      ? "No customer matches these filters."
                      : `No ${q.orderType.toLowerCase()} customers yet.`
                  }
                  emptyHint={
                    q.query || q.date
                      ? "Clear the date or the search to widen the list."
                      : "Customers appear here as soon as an order is entered."
                  }
                />
                {!q.loading &&
                  !q.loadError &&
                  rows.map((row) =>
                    row.group ? (
                      <tr className="hx-grouprow" key={"g" + row.group}>
                        <td colSpan={COLUMNS.length}>
                          <div className="hx-group">
                            <span className="hx-group-d">{row.label}</span>
                            <span className="hx-group-n">
                              {row.count} {row.count === 1 ? "order" : "orders"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <CustomerRow
                        key={row.order._id || row.index}
                        order={row.order}
                        orderType={q.orderType}
                        num={q.listStart + row.index + 1}
                      />
                    )
                  )}
              </tbody>
            </table>
          </div>
          <HistoryFoot q={q} />
        </div>
      </div>
    </div>
  );
}

export default CustomersHistoryRedesign;
